import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import SearchableSelect from '../components/SearchableSelect';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';

const ORDER_TYPES = ['All', 'Production Orders', 'Purchase Orders', 'Inventory Transfer Requests', 'Purchase Quotations', 'Purchase Requests'];
const ORDER_TYPE_MAP = {
  'All': 'All',
  'Production Orders': 'Production Order',
  'Purchase Orders': 'Purchase Order',
  'Inventory Transfer Requests': 'Inventory Transfer',
  'Purchase Quotations': 'Purchase Quotation',
  'Purchase Requests': 'Purchase Request',
};

/** Editable order types on each recommendation row */
const DOC_TYPE_OPTIONS = [
  'Purchase Order',
  'Production Order',
  'Inventory Transfer',
  'Purchase Quotation',
  'Purchase Request',
];

const PURCHASE_DOC_TYPES = ['Purchase Order', 'Purchase Request', 'Purchase Quotation'];

/** MRP rec_id from API may be number or string — use for Set/compare. */
function normRecId(id) {
  if (id == null || id === '') return null;
  const n = Number(id);
  return Number.isFinite(n) ? n : String(id);
}

/**
 * What <input type="date"> wants: yyyy-mm-dd, and empty for nothing set.
 *
 * MySQL dates arrive as "2026-09-30T00:00:00.000Z" over JSON, which the input
 * silently refuses, leaving a saved date looking unsaved.
 */
function toDateInputValue(value) {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

/** Object map key for MRP rec_id (always string — avoids missing lookups). */
function recKey(recId) {
  if (recId == null || recId === '') return '';
  return String(recId);
}

/**
 * What MRP recommended — for every document type alike.
 *
 * This used to cap an inventory transfer at the free stock left at its source, so a
 * recommendation of 13 against a source since drained read as 0. A planner seeing 0
 * under a column headed Quantity concludes MRP asked for nothing; 116 of 131 rows on
 * one run read that way. The general Excel export never capped - it has always written
 * recommended_qty - so the screen and that file disagreed with each other.
 *
 * Nothing about how MRP decides has changed. This is only what the number on screen
 * says it is. Availability is still shown, by transferShortfall below, where it reads
 * as the warning it is instead of quietly replacing the figure.
 */
function displayOrderRecQty(rec) {
  const raw = Number(rec.recommended_qty);
  if (!Number.isFinite(raw)) return '—';
  return Math.ceil(raw);
}

/**
 * How much the source can supply today, when that is less than MRP asked for.
 *
 * Null when there is enough, so an ordinary row stays uncluttered. A transfer whose
 * source cannot cover it is refused on Accept - the accept handler clamps to free
 * stock and stops - so this is what makes that refusal expected rather than a
 * surprise.
 */
function transferShortfall(rec) {
  if (rec.doc_type !== 'Inventory Transfer') return null;
  const raw = Number(rec.recommended_qty);
  const freeRaw = rec.from_wh_available;
  const free = freeRaw != null && freeRaw !== '' ? Number(freeRaw) : NaN;
  if (!Number.isFinite(raw) || !Number.isFinite(free)) return null;
  if (free >= raw - 1e-9) return null;
  return { free: Math.max(0, free), asked: raw };
}

/**
 * The quantity, tinted when the source cannot supply all of it, explained on hover.
 *
 * No second number sits beside it: SAP's own screen shows the quantity alone, and
 * this project is judged on matching SAP, so a figure ours carries and SAP's does not
 * invites "why is yours different" from people who work in both. The two are also
 * different kinds of number - the quantity is what MRP decided when the run happened,
 * free stock is what is there right now - so standing them together implied a
 * relationship they do not have.
 *
 * The tint stays, because accepting a recommendation for 168 and receiving a transfer
 * of 24 needs warning. The detail is on hover, written for someone who does not work
 * in the system every day: which warehouses, what is physically there, how much is
 * already promised, and what pressing Accept will actually do.
 *
 * Rendered through a portal because the table scrolls; a card inside the cell would be
 * clipped at its edge.
 */
function QtyWithShortfall({ rec }) {
  const short = transferShortfall(rec);
  const ref = useRef(null);
  const [at, setAt] = useState(null);

  if (!short) {
    return <span className="text-slate-800">{displayOrderRecQty(rec)}</span>;
  }

  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setAt({
      top: Math.min(r.bottom + 6, window.innerHeight - 230),
      left: Math.max(8, Math.min(r.left - 120, window.innerWidth - 348)),
    });
  };

  const uom = rec.uom || '';
  const inStock = Number(rec.from_wh_in_stock);
  const committed = Number(rec.from_wh_committed);
  const haveHalves = Number.isFinite(inStock) && Number.isFinite(committed);
  const n = (v) => Number(v).toLocaleString('en-IN', { maximumFractionDigits: 4 });
  // The cell rounds the requirement up, so the card does too; a card that disagreed
  // with the figure it is explaining would be worse than no card.
  const asked = Math.ceil(short.asked);
  const shortBy = asked - short.free;

  const Row = ({ label, value, note, strong }) => (
    <tr>
      <td className="py-0.5 pr-3 text-slate-500 whitespace-nowrap">{label}</td>
      <td className={`py-0.5 text-right tabular-nums whitespace-nowrap ${
        strong ? 'font-semibold text-amber-700' : 'text-slate-800'}`}>
        {value} {uom}
      </td>
      <td className="py-0.5 pl-2 text-slate-400 whitespace-nowrap">{note || ''}</td>
    </tr>
  );

  return (
    <>
      <span
        ref={ref}
        className="text-amber-700 cursor-help underline decoration-dotted underline-offset-2"
        onMouseEnter={show}
        onMouseLeave={() => setAt(null)}
      >
        {displayOrderRecQty(rec)}
      </span>
      {at && createPortal(
        <div
          className="fixed z-[300] w-[340px] rounded-lg border border-amber-300 bg-white shadow-xl p-3 text-[11px] leading-snug text-left font-normal"
          style={{ top: at.top, left: at.left }}
        >
          <div className="font-semibold text-amber-800 mb-1.5">
            This cannot be transferred in full
          </div>

          <div className="text-slate-600">
            <span className="font-medium text-slate-800">{rec.item_no}</span>
            {rec.item_desc ? ` — ${rec.item_desc}` : ''}
          </div>
          <div className="text-slate-600 mb-2">
            From <span className="font-medium text-slate-800">
              {rec.from_wh_code || '?'}{rec.from_wh_name ? ` — ${rec.from_wh_name}` : ''}
            </span>
            <br />
            To <span className="font-medium text-slate-800">
              {rec.to_wh_code || '?'}{rec.to_wh_name ? ` — ${rec.to_wh_name}` : ''}
            </span>
          </div>

          <table className="w-full border-t border-slate-200">
            <tbody>
              <Row label="MRP needs" value={n(asked)} />
              {haveHalves && <Row label="Physically in stock" value={n(inStock)} note="at the source" />}
              {haveHalves && <Row label="Already promised" value={n(committed)} note="to other orders" />}
              <Row label="Free to move" value={n(short.free)} strong />
              <Row label="Short by" value={n(shortBy)} strong />
            </tbody>
          </table>

          <div className="mt-2 pt-2 border-t border-slate-200 text-slate-600">
            {short.free > 0 ? (
              <>
                Accepting this creates a transfer of{' '}
                <span className="font-semibold text-slate-800">{n(short.free)} {uom}</span>.
                The remaining{' '}
                <span className="font-semibold text-slate-800">{n(shortBy)} {uom}</span>{' '}
                will appear again on the next MRP run.
              </>
            ) : (
              <>
                Nothing is free to move, so this transfer cannot be accepted yet. It will
                appear again on the next MRP run once stock reaches{' '}
                <span className="font-semibold text-slate-800">{rec.from_wh_code}</span>.
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

function inventoryTransferQtyHint(rec) {
  const short = transferShortfall(rec);
  if (!short) return undefined;
  return `MRP recommended ${short.asked}. The source warehouse currently has `
    + `${short.free} free (in stock minus committed), so this cannot be accepted in `
    + `full until stock arrives.`;
}

export default function OrderRecommendation() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role_name === 'Super Admin';

  /* ── Selection Criteria state ── */
  const [showCriteria, setShowCriteria] = useState(true);
  const [runs, setRuns] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [criteria, setCriteria] = useState({
    orderType: 'All',
    scenario: '',
    dueDateFrom: '', dueDateTo: '',
    releaseDateFrom: '', releaseDateTo: '',
    itemCodeFrom: '', itemCodeTo: '',
    itemGroup: 'All',
    vendorCodeFrom: '', vendorCodeTo: '',
    vendorGroup: 'All',
    itemProperties: 'Ignore',
    vendorProperties: 'Ignore',
  });
  const [appliedCriteria, setAppliedCriteria] = useState(null);

  /* ── Results state ── */
  const [recs, setRecs] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [message, setMessage] = useState(null);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [modalVendorMap, setModalVendorMap] = useState({});
  const [showItModal, setShowItModal] = useState(false);
  const [itWarehouses, setItWarehouses] = useState([]);
  const [itToByRecId, setItToByRecId] = useState({});
  /** Bulk filter: empty = all modal lines are included in bulk actions */
  const [itBulkFromWarehouseId, setItBulkFromWarehouseId] = useState('');
  /** rec_id key (normRecId) → include this line in Save & in bulk “To warehouse” */
  const [itModalRowChecked, setItModalRowChecked] = useState({});
  /** Controlled reset for bulk destination dropdown after applying */
  const [itBulkToValue, setItBulkToValue] = useState('');
  const itModalCheckedRef = useRef({});
  useEffect(() => {
    itModalCheckedRef.current = itModalRowChecked;
  }, [itModalRowChecked]);

  const [itSaving, setItSaving] = useState(false);
  const [itSummary, setItSummary] = useState(null);
  const [acceptSummary, setAcceptSummary] = useState(null);
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('Planned');
  const [typeFilter, setTypeFilter] = useState('All');
  const [savingRecId, setSavingRecId] = useState(null);
  const [visibleCount, setVisibleCount] = useState(100);

  useEffect(() => {
    setVisibleCount(100);
  }, [statusFilter, typeFilter, recs]);

  const vendorOptions = useMemo(
    () => {
      const sorted = [...vendors].sort((a, b) => {
        const nameA = String(a.vendor_name || '').trim().toLowerCase();
        const nameB = String(b.vendor_name || '').trim().toLowerCase();
        return nameA.localeCompare(nameB);
      });
      return sorted.map((v) => ({
        value: String(v.vendor_id),
        label: `${v.vendor_name} (${v.vendor_code})`,
        buttonLabel: v.vendor_code,
        searchText: `${v.vendor_name} ${v.vendor_code}`,
      }));
    },
    [vendors]
  );

  const modalVendorOptions = useMemo(
    () => {
      const sorted = [...vendors].sort((a, b) => {
        const nameA = String(a.vendor_name || '').trim().toLowerCase();
        const nameB = String(b.vendor_name || '').trim().toLowerCase();
        return nameA.localeCompare(nameB);
      });
      return sorted.map((v) => ({
        value: String(v.vendor_id),
        label: `${v.vendor_name} (${v.vendor_code})`,
        buttonLabel: v.vendor_name ? `${v.vendor_name} (${v.vendor_code})` : v.vendor_code,
        searchText: `${v.vendor_name} ${v.vendor_code}`,
      }));
    },
    [vendors]
  );

  /* ── Load runs + vendors on mount ── */
  useEffect(() => {
    Promise.all([
      api.get('/mrp/runs'),
      api.get('/vendors?limit=2500'),
    ]).then(([runsData, vendorsData]) => {
      const r = Array.isArray(runsData) ? runsData : runsData?.rows || [];
      setRuns(r);
      const v = Array.isArray(vendorsData) ? vendorsData : vendorsData?.rows || [];
      setVendors(v);
    }).catch(() => { });
  }, []);

  const handleUpdateRec = useCallback(async (recId, patch) => {
    setSavingRecId(recId);
    try {
      const updated = await api.patch(`/mrp/recommendation/${recId}`, patch);
      setRecs((prev) => prev.map((row) => {
        if (row.rec_id !== recId) return row;
        return {
          ...row,
          ...updated,
          item_desc: updated.item_desc ?? row.item_desc,
        };
      }));
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Could not save recommendation' });
    } finally {
      setSavingRecId(null);
    }
  }, []);

  /* ── Apply criteria and load recommendations ── */
  const handleOK = useCallback(() => {
    if (!criteria.scenario) return;
    setShowCriteria(false);
    setAppliedCriteria({ ...criteria });
    setLoading(true);
    setSelected(new Set());
    setMessage(null);
    api.get(`/mrp/recommendations/${criteria.scenario}`)
      .then(data => {
        let rows = data.recommendations || [];
        const c = criteria;
        // Filter by order type
        if (c.orderType !== 'All') {
          const mapped = ORDER_TYPE_MAP[c.orderType] || c.orderType;
          rows = rows.filter(r => r.doc_type === mapped);
        }
        // Filter by due date range
        if (c.dueDateFrom) rows = rows.filter(r => r.required_date && r.required_date.slice(0, 10) >= c.dueDateFrom);
        if (c.dueDateTo) rows = rows.filter(r => r.required_date && r.required_date.slice(0, 10) <= c.dueDateTo);
        // Filter by release date range
        if (c.releaseDateFrom) rows = rows.filter(r => r.release_date && r.release_date.slice(0, 10) >= c.releaseDateFrom);
        if (c.releaseDateTo) rows = rows.filter(r => r.release_date && r.release_date.slice(0, 10) <= c.releaseDateTo);
        // Filter by item code range
        if (c.itemCodeFrom) rows = rows.filter(r => (r.item_no || '') >= c.itemCodeFrom);
        if (c.itemCodeTo) rows = rows.filter(r => (r.item_no || '') <= c.itemCodeTo);
        // Filter by vendor code range
        if (c.vendorCodeFrom) rows = rows.filter(r => (r.vendor_code || '') >= c.vendorCodeFrom);
        if (c.vendorCodeTo) rows = rows.filter(r => (r.vendor_code || '') <= c.vendorCodeTo);
        setRecs(rows);
      })
      .catch((e) => {
        setRecs([]);
        setMessage({ type: 'error', text: e.message || 'Could not load recommendations' });
      })
      .finally(() => setLoading(false));
  }, [criteria]);

  const handleCancel = () => {
    if (appliedCriteria) {
      setShowCriteria(false);
    }
  };

  const loadRecs = useCallback((keepMessage = false) => {
    if (!appliedCriteria?.scenario) return;
    setCriteria(appliedCriteria);
    setShowCriteria(false);
    setLoading(true);
    setSelected(new Set());
    if (!keepMessage) setMessage(null);
    api.get(`/mrp/recommendations/${appliedCriteria.scenario}`)
      .then(data => {
        let rows = data.recommendations || [];
        const c = appliedCriteria;
        if (c.orderType !== 'All') {
          const mapped = ORDER_TYPE_MAP[c.orderType] || c.orderType;
          rows = rows.filter(r => r.doc_type === mapped);
        }
        if (c.dueDateFrom) rows = rows.filter(r => r.required_date && r.required_date.slice(0, 10) >= c.dueDateFrom);
        if (c.dueDateTo) rows = rows.filter(r => r.required_date && r.required_date.slice(0, 10) <= c.dueDateTo);
        if (c.releaseDateFrom) rows = rows.filter(r => r.release_date && r.release_date.slice(0, 10) >= c.releaseDateFrom);
        if (c.releaseDateTo) rows = rows.filter(r => r.release_date && r.release_date.slice(0, 10) <= c.releaseDateTo);
        if (c.itemCodeFrom) rows = rows.filter(r => (r.item_no || '') >= c.itemCodeFrom);
        if (c.itemCodeTo) rows = rows.filter(r => (r.item_no || '') <= c.itemCodeTo);
        if (c.vendorCodeFrom) rows = rows.filter(r => (r.vendor_code || '') >= c.vendorCodeFrom);
        if (c.vendorCodeTo) rows = rows.filter(r => (r.vendor_code || '') <= c.vendorCodeTo);
        setRecs(rows);
      })
      .catch((e) => {
        setRecs([]);
        setMessage({ type: 'error', text: e.message || 'Could not load recommendations' });
      })
      .finally(() => setLoading(false));
  }, [appliedCriteria]);

  /* ── Checkbox toggles ── */
  const toggleAll = (e) => {
    if (e.target.checked) {
      setSelected(prev => { const next = new Set(prev); filtered.forEach(r => next.add(r.rec_id)); return next; });
    } else {
      setSelected(prev => { const next = new Set(prev); filtered.forEach(r => next.delete(r.rec_id)); return next; });
    }
  };
  const toggleRow = (id) => {
    setSelected(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };

  /* ── Accept selected ── */
  const handleAccept = () => {
    if (selected.size === 0) return;
    const hasPO = [...selected].some(id => {
      const r = recs.find(x => x.rec_id === id);
      return r && r.status === 'Planned' && PURCHASE_DOC_TYPES.includes(r.doc_type);
    });
    if (hasPO) {
      const init = {};
      for (const id of selected) {
        const r = recs.find((x) => x.rec_id === id);
        if (r?.vendor_id) init[id] = String(r.vendor_id);
      }
      setModalVendorMap(init);
      setShowVendorModal(true);
    } else {
      executeAccept({});
    }
  };

  const executeAccept = async (vendorAssignments) => {
    setAccepting(true);
    setMessage(null);
    setAcceptSummary(null);
    let skippedIt = 0;
    const errors = [];
    const acceptedRecKeys = [];
    const summary = { purchase_orders: [], production_orders: [], errors: [], skippedIt: 0 };
    const poBatch = [];
    const prodRecs = [];

    for (const rec_id of selected) {
      const rec = recs.find((r) => r.rec_id === rec_id);
      if (!rec || rec.status !== 'Planned') continue;
      if (rec.doc_type === 'Inventory Transfer') {
        skippedIt += 1;
        continue;
      }
      if (PURCHASE_DOC_TYPES.includes(rec.doc_type)) {
        const vid = vendorAssignments[rec_id] || rec.vendor_id;
        if (!vid) {
          errors.push(`${rec.item_no}: select a vendor`);
          summary.errors.push({ item_no: rec.item_no, message: 'select a vendor' });
          continue;
        }
        poBatch.push({ rec_id: Number(rec_id), vendor_id: parseInt(vid, 10) });
      } else if (rec.doc_type === 'Production Order') {
        prodRecs.push(rec);
      }
    }

    if (poBatch.length > 0) {
      try {
        const batch = await api.post('/mrp/accept-batch', { items: poBatch });
        for (const po of batch.purchase_orders || []) {
          summary.purchase_orders.push(po);
          for (const rid of po.rec_ids || []) {
            const k = normRecId(rid);
            if (k != null) acceptedRecKeys.push(k);
          }
        }
        for (const err of batch.errors || []) {
          errors.push(`${err.item_no || err.rec_id}: ${err.message}`);
          summary.errors.push(err);
        }
      } catch (e) {
        errors.push(e.message || 'Batch accept failed');
      }
    }

    for (const rec of prodRecs) {
      try {
        const data = await api.post('/mrp/accept', { rec_id: Number(rec.rec_id) });
        summary.production_orders.push({
          order_no: data.order_no,
          item_no: data.item_no,
          item_desc: data.item_desc,
          quantity: data.quantity,
          linked_so_number: data.linked_so_number,
          project_no: data.project_no,
          distr_rule: data.distr_rule,
        });
        const k = normRecId(rec.rec_id);
        if (k != null) acceptedRecKeys.push(k);
      } catch (e) {
        errors.push(`${rec.item_no}: ${e.message}`);
        summary.errors.push({ item_no: rec.item_no, message: e.message });
      }
    }

    if (acceptedRecKeys.length > 0) {
      const drop = new Set(acceptedRecKeys);
      setRecs((prev) => prev.filter((row) => !drop.has(normRecId(row.rec_id))));
      setSelected(new Set());
      setStatusFilter('Planned');
    }

    setAccepting(false);
    summary.skippedIt = skippedIt;

    const createdCount = summary.purchase_orders.reduce((n, po) => n + (po.line_count || 0), 0)
      + summary.production_orders.length;

    if (createdCount > 0) {
      setAcceptSummary(summary);
    }

    const itHint = skippedIt > 0
      ? ` ${skippedIt} inventory transfer line(s) skipped — use Inventory transfer to move stock.`
      : '';
    if (errors.length > 0 && createdCount === 0) {
      setMessage({ type: 'error', text: `${errors.join('; ')}${itHint}` });
    } else if (errors.length > 0) {
      setMessage({ type: 'error', text: `Some lines failed: ${errors.join('; ')}${itHint}` });
    } else if (createdCount === 0 && skippedIt > 0) {
      setMessage({ type: 'error', text: `Select Inventory transfer to process ${skippedIt} transfer recommendation(s).` });
    }
    loadRecs(true);
  };

  /* ── Filtered rows (status + type filter on already-criteria-filtered results) ── */
  const filtered = recs.filter(r => {
    if (statusFilter !== 'All' && r.status !== statusFilter) return false;
    if (typeFilter !== 'All' && r.doc_type !== typeFilter) return false;
    return true;
  }).sort((a, b) => {
    const nameA = String(a.vendor_name || '').trim().toLowerCase();
    const nameB = String(b.vendor_name || '').trim().toLowerCase();
    if (!nameA && nameB) return 1; // Put empty vendor names at the end
    if (nameA && !nameB) return -1;
    if (!nameA && !nameB) {
      // Fallback if both empty: sort by required_date then item_no
      const dateA = a.required_date || '';
      const dateB = b.required_date || '';
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      return String(a.item_no || '').localeCompare(String(b.item_no || ''));
    }
    if (nameA !== nameB) {
      return nameA.localeCompare(nameB);
    }
    // Secondary sort: required_date or item_no so they are stable
    const dateA = a.required_date || '';
    const dateB = b.required_date || '';
    if (dateA !== dateB) return dateA.localeCompare(dateB);
    return String(a.item_no || '').localeCompare(String(b.item_no || ''));
  });

  const handleExportExcel = () => {
    if (!filtered || filtered.length === 0) {
      alert("No records to export.");
      return;
    }

    const wb = XLSX.utils.book_new();

    // Group by doc_type
    const groups = {};
    filtered.forEach(r => {
      const type = r.doc_type || 'Other';
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(r);
    });

    // Generate sheets for each group
    Object.keys(groups).forEach(docType => {
      const items = groups[docType];
      let sheetData = [];

      if (docType === 'Purchase Order' || docType === 'Purchase Quotation' || docType === 'Purchase Request') {
        sheetData = items.map((r, index) => {
          const priceVal = Number(r.unit_price) || 0;
          const qtyVal = Number(r.recommended_qty) || 0;
          return {
            'S.No.': index + 1,
            'Item Code': r.item_no || '',
            'Description': r.item_desc || '',
            'Qty': qtyVal,
            'UoM': r.uom || '',
            'UoM Name': r.uom_name || '',
            'Price (INR)': priceVal,
            'Discount %': 0.0000,
            'Price After Discount (INR)': priceVal,
            'Total Value (INR)': qtyVal * priceVal,
            'Vendor Code': r.vendor_code || r.preferred_vendor || '',
            'Vendor Name': r.vendor_name || '',
            'Lead Time (Days)': r.lead_time_days ?? 0,
            'Due Date': r.required_date ? new Date(r.required_date).toLocaleDateString('en-GB') : '',
            'Release Date': r.release_date ? new Date(r.release_date).toLocaleDateString('en-GB') : '',
            'Critical Item Delivery Date': r.critical_item_delivery_date
              ? new Date(r.critical_item_delivery_date).toLocaleDateString('en-GB') : '',
            'Linked SO': r.linked_so_number || (r.linked_so_id ? `#${r.linked_so_id}` : ''),
            'Project Code': r.inherited_project_no || '',
            'Distribution Rule': r.inherited_distr_rule || '',
            'Doc Type': r.doc_type || '',
            'Status': r.status || ''
          };
        });
      } else if (docType === 'Production Order') {
        sheetData = items.map((r, index) => ({
          'S.No.': index + 1,
          'Item Code': r.item_no || '',
          'Description': r.item_desc || '',
          'Qty': Number(r.recommended_qty) || 0,
          'UoM': r.uom || '',
          'Required Date': r.required_date ? new Date(r.required_date).toLocaleDateString('en-GB') : '',
          'Procurement Method': r.procurement_method || 'Make',
          'Linked SO': r.linked_so_number || (r.linked_so_id ? `#${r.linked_so_id}` : ''),
          'Project Code': r.inherited_project_no || '',
          'Distribution Rule': r.inherited_distr_rule || '',
          'Status': r.status || ''
        }));
      } else if (docType === 'Inventory Transfer') {
        sheetData = items.map((r, index) => ({
          'S.No.': index + 1,
          'Item Code': r.item_no || '',
          'Description': r.item_desc || '',
          'Qty': displayOrderRecQty(r),
          'UoM': r.uom || '',
          'Required Date': r.required_date ? new Date(r.required_date).toLocaleDateString('en-GB') : '',
          'From Warehouse': r.from_wh_code ? `${r.from_wh_name || ''} (${r.from_wh_code})` : '',
          'To Warehouse': r.to_wh_code ? `${r.to_wh_name || ''} (${r.to_wh_code})` : '',
          'Available at Source': Number(r.from_wh_available) || 0,
          'Status': r.status || ''
        }));
      } else {
        sheetData = items.map((r, index) => ({
          'S.No.': index + 1,
          'Item Code': r.item_no || '',
          'Description': r.item_desc || '',
          'Qty': Number(r.recommended_qty) || 0,
          'UoM': r.uom || '',
          'Required Date': r.required_date ? new Date(r.required_date).toLocaleDateString('en-GB') : '',
          'Doc Type': r.doc_type || '',
          'Procurement Method': r.procurement_method || '',
          'Status': r.status || ''
        }));
      }

      const ws = XLSX.utils.json_to_sheet(sheetData);

      // Set column widths to prevent clipping
      const cols = [];
      if (sheetData.length > 0) {
        Object.keys(sheetData[0]).forEach(key => {
          let maxLen = key.toString().length;
          sheetData.forEach(row => {
            const val = row[key] ? row[key].toString() : '';
            if (val.length > maxLen) maxLen = val.length;
          });
          cols.push({ wch: Math.min(Math.max(maxLen + 2, 8), 50) });
        });
        ws['!cols'] = cols;
      }

      const cleanSheetName = docType.substring(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, cleanSheetName);
    });

    const dateStr = new Date().toISOString().slice(0, 10);
    const runName = selectedScenario?.run_name || `Scenario_${appliedCriteria?.scenario}`;
    const fileName = `Order_Recommendations_${runName}_${dateStr}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const handleDeleteRun = async () => {
    const runId = appliedCriteria?.scenario;
    if (!runId) return;

    const runName = selectedScenario?.run_name || `Run #${runId}`;
    if (!window.confirm(`Are you sure you want to delete the whole order recommendation for scenario "${runName}"? This will permanently delete the run and all its recommendations.`)) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/mrp/run/${runId}`);
      setMessage({ type: 'success', text: `Scenario run "${runName}" and all recommendations deleted successfully.` });

      // Clear data
      setRecs([]);
      setSelected(new Set());
      setAppliedCriteria(null);
      setShowCriteria(true);

      // Refresh runs dropdown
      const runsData = await api.get('/mrp/runs');
      const r = Array.isArray(runsData) ? runsData : runsData?.rows || [];
      setRuns(r);
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Could not delete scenario run' });
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 150) {
      setVisibleCount(prev => Math.min(prev + 100, filtered.length));
    }
  }, [filtered.length]);

  const startIdx = 0;
  const paged = filtered.slice(0, visibleCount);
  const pendingSelected = useMemo(
    () =>
      [...selected].filter((id) => {
        const r = recs.find((x) => x.rec_id === id);
        return r && r.status === 'Planned';
      }),
    [selected, recs]
  );
  const plannedItSelected = useMemo(
    () =>
      pendingSelected
        .map((id) => recs.find((x) => x.rec_id === id))
        .filter((r) => r && r.doc_type === 'Inventory Transfer'),
    [pendingSelected, recs]
  );

  /** Distinct source warehouses in the IT modal (for bulk From filter) */
  const itModalFromOptions = useMemo(() => {
    const m = new Map();
    for (const r of plannedItSelected) {
      const id = r.from_warehouse_id;
      if (id == null || id === '') continue;
      const k = String(id);
      if (m.has(k)) continue;
      const label = [r.from_wh_code, r.from_wh_name].filter(Boolean).join(' · ') || `Warehouse ${k}`;
      m.set(k, { id: k, label });
    }
    return [...m.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [plannedItSelected]);

  const applyBulkFromWarehouse = useCallback((fromId) => {
    setItBulkFromWarehouseId(fromId);
    const next = {};
    for (const r of plannedItSelected) {
      const k = normRecId(r.rec_id);
      if (k == null) continue;
      if (!fromId) next[k] = true;
      else next[k] = String(r.from_warehouse_id ?? '') === fromId;
    }
    setItModalRowChecked(next);
    itModalCheckedRef.current = next;
  }, [plannedItSelected]);

  const applyBulkToWarehouse = useCallback(
    (toId) => {
      if (!toId) return;
      const chk = itModalCheckedRef.current;
      setItToByRecId((prev) => {
        const m = { ...prev };
        for (const r of plannedItSelected) {
          const k = normRecId(r.rec_id);
          if (k != null && chk[k] === false) continue;
          if (String(r.from_warehouse_id ?? '') === String(toId)) continue;
          m[recKey(r.rec_id)] = toId;
        }
        return m;
      });
    },
    [plannedItSelected]
  );

  const openInventoryTransferModal = async () => {
    if (plannedItSelected.length === 0) return;
    setItSummary(null);
    try {
      const wh = await api.get('/mrp/warehouses');
      setItWarehouses(Array.isArray(wh) ? wh : []);
    } catch {
      setItWarehouses([]);
    }
    const map = {};
    const checkedInit = {};
    plannedItSelected.forEach((r) => {
      map[recKey(r.rec_id)] = r.to_warehouse_id != null ? String(r.to_warehouse_id) : '';
      const nk = normRecId(r.rec_id);
      if (nk != null) checkedInit[nk] = true;
    });
    setItToByRecId(map);
    setItModalRowChecked(checkedInit);
    itModalCheckedRef.current = checkedInit;
    setItBulkFromWarehouseId('');
    setItBulkToValue('');
    setShowItModal(true);
  };

  const executeInventoryTransfers = async () => {
    const rows = plannedItSelected.filter((r) => {
      const k = normRecId(r.rec_id);
      if (k == null) return true;
      return itModalRowChecked[k] !== false;
    });
    const results = [];
    const errors = [];
    setItSaving(true);
    try {
      for (const rec of rows) {
        const toId = itToByRecId[recKey(rec.rec_id)];
        if (!toId) {
          errors.push(`${rec.item_no || '—'}: choose destination warehouse`);
          continue;
        }
        try {
          const rid = Number(rec.rec_id);
          const data = await api.post('/mrp/accept', {
            rec_id: Number.isFinite(rid) ? rid : rec.rec_id,
            to_warehouse_id: parseInt(toId, 10),
          });
          results.push({ ...(data || {}), rec_id: rec.rec_id, item_no: rec.item_no });
        } catch (e) {
          errors.push(`${rec.item_no || '—'}: ${e.message || 'failed'}`);
        }
      }
      setShowItModal(false);
      setItToByRecId({});
      setItModalRowChecked({});
      setItBulkFromWarehouseId('');
      setItBulkToValue('');
      setItSummary({ results, errors });
      const doneKeys = new Set(results.map((r) => normRecId(r.rec_id)).filter((k) => k != null));
      if (doneKeys.size > 0) {
        setRecs((prev) => prev.filter((row) => !doneKeys.has(normRecId(row.rec_id))));
        setStatusFilter('Planned');
      }
      setSelected((prev) => {
        const next = new Set(prev);
        for (const id of [...next]) {
          if (doneKeys.has(normRecId(id))) next.delete(id);
        }
        return next;
      });
      loadRecs(true);
    } finally {
      setItSaving(false);
    }
  };

  const selectedScenario = runs.find(r => String(r.run_id) === String(appliedCriteria?.scenario));

  /**
   * A run that planned only some of an order's lines, said so by the run itself.
   *
   * The planner needs to know which scenario is which before opening it: a partial
   * run's recommendations cover part of the order and look, at a glance, like a full
   * run that came up short.
   *
   * This does not try to notice that the run's name already says "Partial". An
   * earlier version did, and hid the label from any run whose name merely contained
   * the word. A repeated word costs a reader nothing; a missing flag costs them the
   * thing they came to find out — and the count beside it is information no
   * hand-typed name carries.
   */
  const scenarioLabel = useCallback((r, withCount) => {
    const name = r.run_name || 'Run';
    const partial = r.is_partial
      ? ` · Partial${r.partial_item_count ? ` (${r.partial_item_count} item${r.partial_item_count === 1 ? '' : 's'})` : ''}`
      : '';
    return `#${r.run_id} · ${name}${partial}${withCount ? ` (${r.rec_count} recs)` : ''}`;
  }, []);

  const scenarioSelectOptions = useMemo(
    () =>
      runs
        .filter((r) => Number(r.rec_count) > 0)
        .map((r) => ({
          value: String(r.run_id),
          label: scenarioLabel(r, false),
        })),
    [runs, scenarioLabel]
  );

  const scenarioSelectOptionsWithCounts = useMemo(
    () =>
      runs
        .filter((r) => Number(r.rec_count) > 0)
        .map((r) => ({
          value: String(r.run_id),
          label: scenarioLabel(r, true),
        })),
    [runs, scenarioLabel]
  );

  /** Switch MRP run from results toolbar (same filters as previous native select). */
  const switchMRPRun = useCallback(
    (newScenario) => {
      if (!newScenario || !appliedCriteria) return;
      const newCriteria = { ...appliedCriteria, scenario: newScenario };
      setAppliedCriteria(newCriteria);
      setCriteria(newCriteria);
      setLoading(true);
      setSelected(new Set());
      setMessage(null);
      api.get(`/mrp/recommendations/${newScenario}`)
        .then((data) => {
          let rows = data.recommendations || [];
          const c = newCriteria;
          if (c.orderType !== 'All') {
            const mapped = ORDER_TYPE_MAP[c.orderType] || c.orderType;
            rows = rows.filter((r) => r.doc_type === mapped);
          }
          if (c.dueDateFrom) rows = rows.filter((r) => r.required_date && r.required_date.slice(0, 10) >= c.dueDateFrom);
          if (c.dueDateTo) rows = rows.filter((r) => r.required_date && r.required_date.slice(0, 10) <= c.dueDateTo);
          if (c.itemCodeFrom) rows = rows.filter((r) => (r.item_no || '') >= c.itemCodeFrom);
          if (c.itemCodeTo) rows = rows.filter((r) => (r.item_no || '') <= c.itemCodeTo);
          setRecs(rows);
        })
        .catch(() => setRecs([]))
        .finally(() => setLoading(false));
    },
    [appliedCriteria]
  );

  const sc = (k, v) => setCriteria(prev => ({ ...prev, [k]: v }));

  /* ────────────────────────────────────────── */
  /* ── SELECTION CRITERIA DIALOG ── */
  /* ────────────────────────────────────────── */
  if (showCriteria) {
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm px-5 py-3.5 flex items-center gap-4">
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <path d="M9 15l2 2 4-4" />
            </svg>
          </div>
          <div className="flex-1">
            <div className="text-2xs text-sap-textLight uppercase tracking-wider font-medium">MRP</div>
            <h1 className="page-title">Order Recommendation - Selection Criteria</h1>
          </div>
        </div>

        {/* Criteria Form */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden max-w-2xl">
          <div className="px-5 py-3 bg-gray-50 text-sap-text border-b border-sap-border text-xs font-semibold">
            Order Recommendation - Selection Criteria
          </div>
          <div className="p-5 space-y-4">
            {/* Order Type */}
            <div className="flex items-center gap-3">
              <label className="w-28 text-xs font-medium text-sap-textLight text-right shrink-0">Order Type</label>
              <select value={criteria.orderType} onChange={e => sc('orderType', e.target.value)}
                className="flex-1 h-8 px-3 text-xs border border-sap-border rounded bg-white
                           focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue">
                {ORDER_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>

            {/* Scenario */}
            <div className="flex items-center gap-3">
              <label className="w-28 text-xs font-medium text-sap-textLight text-right shrink-0" htmlFor="or-scenario-select">
                Scenario
              </label>
              <SearchableSelect
                id="or-scenario-select"
                className="flex-1 min-w-0"
                buttonClassName="border-sap-border focus:ring-sap-blue/30 focus:border-sap-blue"
                options={scenarioSelectOptions}
                value={criteria.scenario}
                onChange={(v) => sc('scenario', v)}
                emptyLabel="— Select Scenario —"
                allowEmpty
                searchPlaceholder="Search scenario…"
              />
            </div>

            {/* Due Date */}
            <div className="flex items-center gap-3">
              <label className="w-28 text-xs font-medium text-sap-textLight text-right shrink-0">Due Date</label>
              <div className="flex items-center gap-2 flex-1">
                <span className="text-xs text-sap-textLight">From</span>
                <input type="date" value={criteria.dueDateFrom} onChange={e => sc('dueDateFrom', e.target.value)}
                  className="h-8 px-2 text-xs border border-sap-border rounded bg-white
                             focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue" />
                <span className="text-xs text-sap-textLight ml-2">To</span>
                <input type="date" value={criteria.dueDateTo} onChange={e => sc('dueDateTo', e.target.value)}
                  className="h-8 px-2 text-xs border border-sap-border rounded bg-white
                             focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue" />
              </div>
            </div>

            {/* Release Date */}
            <div className="flex items-center gap-3">
              <label className="w-28 text-xs font-medium text-sap-textLight text-right shrink-0">Release Date</label>
              <div className="flex items-center gap-2 flex-1">
                <span className="text-xs text-sap-textLight">From</span>
                <input type="date" value={criteria.releaseDateFrom} onChange={e => sc('releaseDateFrom', e.target.value)}
                  className="h-8 px-2 text-xs border border-sap-border rounded bg-white
                             focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue" />
                <span className="text-xs text-sap-textLight ml-2">To</span>
                <input type="date" value={criteria.releaseDateTo} onChange={e => sc('releaseDateTo', e.target.value)}
                  className="h-8 px-2 text-xs border border-sap-border rounded bg-white
                             focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue" />
              </div>
            </div>

            {/* ── Items Section ── */}
            <div className="border border-sap-border/50 rounded-lg p-3 space-y-3 mt-2">
              <span className="text-xs font-semibold text-sap-text">Items</span>
              <div className="flex items-center gap-3">
                <label className="w-20 text-xs font-medium text-sap-textLight text-right shrink-0">Code</label>
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-xs text-sap-textLight">From</span>
                  <input type="text" value={criteria.itemCodeFrom} onChange={e => sc('itemCodeFrom', e.target.value)}
                    placeholder="" className="w-32 h-7 px-2 text-xs border border-sap-border rounded bg-white
                               focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue" />
                  <span className="text-xs text-sap-textLight ml-2">To</span>
                  <input type="text" value={criteria.itemCodeTo} onChange={e => sc('itemCodeTo', e.target.value)}
                    placeholder="" className="w-32 h-7 px-2 text-xs border border-sap-border rounded bg-white
                               focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="w-20 text-xs font-medium text-sap-textLight text-right shrink-0">Group</label>
                <select value={criteria.itemGroup} onChange={e => sc('itemGroup', e.target.value)}
                  className="flex-1 h-7 px-2 text-xs border border-sap-border rounded bg-white
                             focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue">
                  <option>All</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <label className="w-20 text-xs font-medium text-sap-textLight text-right shrink-0">Properties</label>
                <select value={criteria.itemProperties} onChange={e => sc('itemProperties', e.target.value)}
                  className="flex-1 h-7 px-2 text-xs border border-sap-border rounded bg-white
                             focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue">
                  <option>Ignore</option>
                </select>
              </div>
            </div>

            {/* ── Vendors Section ── */}
            <div className="border border-sap-border/50 rounded-lg p-3 space-y-3">
              <span className="text-xs font-semibold text-sap-text">Vendors</span>
              <div className="flex items-center gap-3">
                <label className="w-20 text-xs font-medium text-sap-textLight text-right shrink-0">Code</label>
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-xs text-sap-textLight">From</span>
                  <input type="text" value={criteria.vendorCodeFrom} onChange={e => sc('vendorCodeFrom', e.target.value)}
                    placeholder="" className="w-32 h-7 px-2 text-xs border border-sap-border rounded bg-white
                               focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue" />
                  <span className="text-xs text-sap-textLight ml-2">To</span>
                  <input type="text" value={criteria.vendorCodeTo} onChange={e => sc('vendorCodeTo', e.target.value)}
                    placeholder="" className="w-32 h-7 px-2 text-xs border border-sap-border rounded bg-white
                               focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="w-20 text-xs font-medium text-sap-textLight text-right shrink-0">Group</label>
                <select value={criteria.vendorGroup} onChange={e => sc('vendorGroup', e.target.value)}
                  className="flex-1 h-7 px-2 text-xs border border-sap-border rounded bg-white
                             focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue">
                  <option>All</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <label className="w-20 text-xs font-medium text-sap-textLight text-right shrink-0">Properties</label>
                <select value={criteria.vendorProperties} onChange={e => sc('vendorProperties', e.target.value)}
                  className="flex-1 h-7 px-2 text-xs border border-sap-border rounded bg-white
                             focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue">
                  <option>Ignore</option>
                </select>
              </div>
            </div>
          </div>

          {/* ── OK / Cancel ── */}
          <div className="px-5 py-3 border-t border-sap-border bg-sap-bg flex items-center gap-2">
            <button type="button" onClick={handleOK} disabled={!criteria.scenario} className="btn-primary disabled:opacity-40">
              OK
            </button>
            <button type="button" onClick={handleCancel} className="btn-secondary text-xs py-1.5 px-4">
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ────────────────────────────────────────── */
  /* ── RESULTS VIEW ── */
  /* ────────────────────────────────────────── */
  return (
    <div className="space-y-4">
      {/* ── Header ── */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm px-5 py-3.5 flex items-center gap-4">
        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <path d="M9 15l2 2 4-4" />
          </svg>
        </div>
        <div className="flex-1">
          <div className="text-2xs text-sap-textLight uppercase tracking-wider font-medium">MRP</div>
          <h1 className="page-title">Order Recommendations</h1>
          {selectedScenario && (
            <p className="text-2xs text-sap-textLight mt-0.5">
              Scenario: <span className="font-semibold text-sap-text">{selectedScenario.run_name}</span>
              {selectedScenario.is_partial && (
                <> · <span className="font-semibold text-sap-gold">
                  Partial run
                  {selectedScenario.partial_item_count
                    ? ` — ${selectedScenario.partial_item_count} item${selectedScenario.partial_item_count === 1 ? '' : 's'} of the order`
                    : ''}
                </span></>
              )}
              {appliedCriteria?.orderType !== 'All' && <> · Type: <span className="font-semibold">{appliedCriteria.orderType}</span></>}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowCriteria(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-sap-border rounded-lg
                       hover:bg-sap-blue/5 transition-colors text-sap-textLight">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            Selection Criteria
          </button>
          <button onClick={handleExportExcel}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#4A9E6E] text-white rounded-lg
                       hover:bg-[#3D8C5C] transition-all font-semibold shadow-sm">
            <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
              <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
            </svg>
            Export to Excel
          </button>
          {isSuperAdmin && (
            <button onClick={handleDeleteRun}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg
                         hover:bg-red-700 transition-all font-semibold shadow-sm">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
              Delete Recommendation
            </button>
          )}
          <button onClick={loadRecs}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-sap-border rounded-lg
                       hover:bg-sap-blue/5 transition-colors text-sap-textLight">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round"><path d="M23 4v6h-6M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
            </svg>
            Refresh
          </button>
          <button
            onClick={handleAccept}
            disabled={accepting || pendingSelected.length === 0}
            className="flex items-center gap-1.5 px-4 py-1.5 text-xs bg-blue-600 text-white rounded-lg
                       hover:bg-blue-700 disabled:opacity-40 transition-colors font-semibold shadow-sm">
            {accepting && <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
            Accept Selected {pendingSelected.length > 0 ? `(${pendingSelected.length})` : ''}
          </button>
          <button
            type="button"
            onClick={openInventoryTransferModal}
            disabled={plannedItSelected.length === 0 || itSaving}
            title="Create inventory transfers for selected MRP transfer lines (choose destination warehouse)"
            className="flex items-center gap-1.5 px-4 py-1.5 text-xs text-white rounded-lg
                       hover:opacity-90 disabled:opacity-40 transition-opacity font-semibold shadow-sm"
            style={{ backgroundColor: '#4A9AF5' }}
          >
            Inventory transfer{plannedItSelected.length > 0 ? ` (${plannedItSelected.length})` : ''}
          </button>
        </div>
      </div>

      {/* ── Message banner ── */}
      {message && (
        <div className={`px-4 py-2.5 text-xs rounded-xl border font-medium
          ${message.type === 'success'
            ? 'bg-sap-blueLight border-sap-blue/30 text-sap-blueDeeper'
            : 'bg-sap-blueLight border-sap-blue/30 text-sap-text'}`}>
          {message.text}
        </div>
      )}

      {/* ── Inline Filters ── */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 flex flex-wrap gap-4 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-2xs font-semibold text-slate-500 uppercase tracking-wide" htmlFor="or-mrp-run-toolbar">
            MRP Run
          </label>
          <SearchableSelect
            id="or-mrp-run-toolbar"
            className="min-w-[180px] max-w-[min(380px,92vw)]"
            buttonClassName="border-slate-300 rounded-lg"
            options={scenarioSelectOptionsWithCounts}
            value={appliedCriteria?.scenario || ''}
            onChange={switchMRPRun}
            emptyLabel="— MRP Run —"
            allowEmpty={false}
            searchPlaceholder="Search MRP run…"
            disabled={!appliedCriteria}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-2xs font-semibold text-slate-500 uppercase tracking-wide">Status</label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="h-8 px-3 text-xs border border-slate-300 rounded-lg bg-white text-slate-800
                       focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10">
            {['All', 'Planned', 'Released', 'Closed'].map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-2xs font-semibold text-slate-500 uppercase tracking-wide">Type</label>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            className="h-8 px-3 text-xs border border-slate-300 rounded-lg bg-white text-slate-800
                       focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10">
            {['All', ...DOC_TYPE_OPTIONS].map(t => <option key={t}>{t}</option>)}
          </select>
        </div>

        <div className="text-xs text-slate-500 self-center pb-0.5">
          {filtered.length} record{filtered.length !== 1 ? 's' : ''}
          {selected.size > 0 && ` · ${selected.size} selected`}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto overflow-y-auto max-h-[600px] relative" onScroll={handleScroll}>
          <table className="text-[11px] w-full">
            <thead className="sticky top-0 bg-gray-50 z-10">
              <tr className="bg-gray-50 text-sap-text border-b border-sap-border">
                <th className="w-6 px-1 py-1 text-center">
                  <input type="checkbox"
                    onChange={toggleAll}
                    checked={filtered.length > 0 && filtered.every(r => selected.has(r.rec_id))}
                    className="accent-blue-400 w-3.5 h-3.5" />
                </th>
                <th className="text-left px-1 py-1 font-medium text-[10px] min-w-[155px] w-[155px]">Order Type</th>
                <th className="text-left px-1 py-1 font-medium text-[10px]">Item Number</th>
                <th className="text-left px-1 py-1 font-medium text-[10px]">Item Description</th>
                <th className="text-right px-1 py-1 font-medium text-[10px]">Quantity</th>
                <th className="text-center px-1 py-1 font-medium text-[10px]">UoM Code</th>
                <th className="text-center px-1 py-1 font-medium text-[10px]">UoM Name</th>
                <th className="text-right px-1 py-1 font-medium text-[10px]">Price</th>
                <th className="text-left px-1 py-1 font-medium text-[10px] min-w-[110px] w-[110px]">Vendor Code</th>
                <th className="text-left px-1 py-1 font-medium text-[10px] min-w-[180px] w-[180px]">Vendor Name</th>
                <th className="text-center px-1 py-1 font-medium text-[10px]">MRP Procurement Method</th>
                <th className="text-right px-1 py-1 font-medium text-[10px]">MRP Order Interval</th>
                <th className="text-right px-1 py-1 font-medium text-[10px]">MRP Order Multiple</th>
                <th className="text-right px-1 py-1 font-medium text-[10px]">MRP Minimum Order Qty</th>
                <th className="text-right px-1 py-1 font-medium text-[10px]">MRP Lead Time</th>
                <th className="text-right px-1 py-1 font-medium text-[10px]">MRP Tolerance Days</th>
                <th className="text-center px-1 py-1 font-medium text-[10px]">MRP Component Warehouse</th>
                <th className="text-center px-1 py-1 font-medium text-[10px]">Release Date</th>
                <th className="text-center px-1 py-1 font-medium text-[10px]">Due Date</th>
                <th className="text-left px-1 py-1 font-medium text-[10px]">Linked SO</th>
                <th className="text-left px-1 py-1 font-medium text-[10px]">Project Code</th>
                <th className="text-left px-1 py-1 font-medium text-[10px]">Distr. Rule</th>
                <th className="text-right px-1 py-1 font-medium text-[10px]">Discount %</th>
                <th className="text-right px-1 py-1 font-medium text-[10px]">Price after Discount</th>
                <th className="text-center px-1 py-1 font-medium text-[10px]">From Whse</th>
                <th className="text-center px-1 py-1 font-medium text-[10px]">To Whse</th>
                <th className="text-right px-1 py-1 font-medium text-[10px]">Qty (Inventory UoM)</th>
                <th className="text-right px-1 py-1 font-medium text-[10px]">Items per Unit</th>
                <th className="text-center px-1 py-1 font-medium text-[10px]">Exception</th>
                <th className="text-center px-1 py-1 font-medium text-[10px]">Blanket Agreement No.</th>
                <th className="text-center px-1 py-1 font-medium text-[10px] min-w-[130px] w-[130px]">Critical Item Delivery Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={31} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={31} className="px-3 py-10 text-center text-slate-400 italic">
                  {recs.length === 0
                    ? 'No recommendations for this MRP run. Run MRP again or pick a different scenario.'
                    : `No recommendations match the current filters (${recs.length} loaded — try Status: All).`}
                </td></tr>
              ) : paged.map((rec, i) => (
                <RecRow
                  key={rec.rec_id}
                  rec={rec}
                  idx={startIdx + i + 1}
                  checked={selected.has(rec.rec_id)}
                  onCheck={() => toggleRow(rec.rec_id)}
                  onNavigateItem={(itemNo) => navigate(`/item-master/${encodeURIComponent(itemNo)}`, { state: { from: 'order-recommendations' } })}
                  onNavigateWhse={(itemNo) => navigate(`/item-master/${encodeURIComponent(itemNo)}?tab=Inventory+Data`, { state: { from: 'order-recommendations' } })}
                  vendorOptions={vendorOptions}
                  onUpdateRec={handleUpdateRec}
                  saving={savingRecId === rec.rec_id}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Scroll Summary ── */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/50">
            <div className="text-xs text-slate-500 font-medium">
              {paged.length < filtered.length
                ? `Showing ${paged.length} of ${filtered.length} recommendations (scroll down to load more)`
                : `Showing all ${filtered.length} recommendation${filtered.length !== 1 ? 's' : ''}`}
            </div>
          </div>
        )}
      </div>

      {/* ── MRP → Inventory Transfer (confirm warehouses) ── */}
      {showItModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/45 p-3">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[min(90vh,720px)] flex flex-col overflow-hidden border border-slate-200/90">
            <div className="px-6 py-5 shrink-0 text-white" style={{ backgroundColor: '#4A9AF5' }}>
              <h2 className="text-base font-bold tracking-tight">Inventory transfer from recommendations</h2>
              <p className="text-xs text-white/90 mt-2 leading-relaxed max-w-3xl">
                Stock moves from the source warehouse into the destination you choose. One transfer document is created per line.
                Quantity matches Item Master <span className="font-semibold">Available</span> at the source (in_stock − committed);
                if inventory changed after MRP ran, qty is capped automatically.
              </p>
            </div>
            <div className="px-4 py-3 overflow-y-auto flex-1 min-h-0 overflow-x-hidden bg-white">
              <div className="flex flex-wrap gap-3 items-end mb-3 pb-3 border-b border-slate-200">
                <div className="flex flex-col gap-1 min-w-0 flex-1 basis-[240px]">
                  <label htmlFor="it-bulk-from-wh" className="text-2xs font-semibold text-slate-500 uppercase tracking-wide">
                    From warehouse
                  </label>
                  <select
                    id="it-bulk-from-wh"
                    value={itBulkFromWarehouseId}
                    onChange={(e) => applyBulkFromWarehouse(e.target.value)}
                    className="w-full min-h-8 pl-2 pr-7 py-1 text-[11px] border border-slate-300 rounded-lg bg-white text-slate-800 shadow-sm
                               focus:outline-none focus:ring-1 focus:ring-sap-blue/30 focus:border-sap-blue appearance-none cursor-pointer"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.35rem center',
                      backgroundSize: '0.875rem',
                    }}
                  >
                    <option value="">All sources — all lines included</option>
                    {itModalFromOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-2xs text-slate-500">
                    Select a source to check only those lines (e.g. 03). Leave on “All sources” to include every line.
                  </p>
                </div>
                <div className="flex flex-col gap-1 min-w-0 flex-1 basis-[240px]">
                  <label htmlFor="it-bulk-to-wh" className="text-2xs font-semibold text-slate-500 uppercase tracking-wide">
                    To warehouse (bulk fill)
                  </label>
                  <div className="flex gap-1.5 items-center">
                    <select
                      id="it-bulk-to-wh"
                      value={itBulkToValue}
                      onChange={(e) => {
                        const v = e.target.value;
                        setItBulkToValue(v);
                        if (v) applyBulkToWarehouse(v);
                      }}
                      className="flex-1 min-h-8 pl-2 pr-7 py-1 text-[11px] border border-slate-300 rounded-lg bg-white text-slate-800 shadow-sm
                                 focus:outline-none focus:ring-1 focus:ring-sap-blue/30 focus:border-sap-blue appearance-none cursor-pointer"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.35rem center',
                        backgroundSize: '0.875rem',
                      }}
                    >
                      <option value="">— Choose destination —</option>
                      {itWarehouses.map((w) => (
                        <option key={w.warehouse_id} value={String(w.warehouse_id)}>
                          {w.wh_code}
                          {w.wh_name ? ` · ${w.wh_name}` : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => applyBulkToWarehouse(itBulkToValue)}
                      disabled={!itBulkToValue}
                      className="px-2.5 py-1 text-2xs font-semibold text-white rounded-lg border border-transparent hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed min-h-8 shrink-0 transition-opacity"
                      style={{ backgroundColor: '#4A9AF5' }}
                    >
                      Apply
                    </button>
                  </div>
                  <p className="text-2xs text-slate-500">
                    Sets “Transfer to” on every <span className="font-semibold text-slate-600">checked</span> line (same as lines matched when you pick From warehouse).
                  </p>
                </div>
              </div>
              <table className="w-full table-fixed text-[11px] border-collapse">
                <colgroup>
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '17%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '25%' }} />
                </colgroup>
                <thead>
                  <tr className="bg-[#F5F5F4] text-slate-700">
                    <th className="text-center font-bold px-1 py-1.5 border border-slate-200" title="Include in bulk fill & save">
                      <span className="sr-only">Include</span>
                    </th>
                    <th className="text-left font-semibold px-1.5 py-1.5 border border-slate-200 leading-tight">Item</th>
                    <th className="text-left font-semibold px-1.5 py-1.5 border border-slate-200 leading-tight">Description</th>
                    <th className="text-center font-semibold px-1 py-1.5 border border-slate-200">Qty</th>
                    <th className="text-left font-semibold px-1.5 py-1.5 border border-slate-200 leading-tight">From whse</th>
                    <th className="text-left font-semibold px-1.5 py-1.5 border border-slate-200 leading-tight">Transfer to</th>
                  </tr>
                </thead>
                <tbody>
                  {plannedItSelected.map((r) => {
                    const rk = normRecId(r.rec_id);
                    const included = rk == null ? true : itModalRowChecked[rk] !== false;
                    const fromMatch =
                      itBulkFromWarehouseId &&
                      String(r.from_warehouse_id ?? '') === itBulkFromWarehouseId;
                    const fromWhLabel =
                      [r.from_wh_code, r.from_wh_name].filter(Boolean).join(' · ') || '—';
                    return (
                      <tr
                        key={r.rec_id}
                        className={`bg-white hover:bg-slate-50/90 ${fromMatch ? 'bg-sap-blueLight/70' : ''
                          }`}
                      >
                        <td className="px-1 py-1.5 border border-slate-200 text-center align-top">
                          <input
                            type="checkbox"
                            className="accent-sap-blue w-3.5 h-3.5 cursor-pointer"
                            checked={included}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              if (rk == null) return;
                              setItModalRowChecked((prev) => {
                                const u = { ...prev, [rk]: checked };
                                itModalCheckedRef.current = u;
                                return u;
                              });
                            }}
                            aria-label={`Include ${r.item_no || 'line'} in transfer`}
                          />
                        </td>
                        <td className="min-w-0 px-1.5 py-1.5 border border-slate-200 align-top">
                          <div className="font-semibold text-slate-800 truncate" title={r.item_no}>{r.item_no}</div>
                        </td>
                        <td className="min-w-0 px-1.5 py-1.5 border border-slate-200 align-top">
                          <div className="text-slate-700 line-clamp-2 leading-snug break-words" title={r.item_desc || ''}>
                            {r.item_desc || '—'}
                          </div>
                        </td>
                        <td
                          className="px-1 py-1.5 border border-slate-200 text-center tabular-nums text-slate-800 font-medium align-top"
                          title={inventoryTransferQtyHint(r)}
                        >
                          {displayOrderRecQty(r)}
                        </td>
                        <td className="min-w-0 px-1.5 py-1.5 border border-slate-200 align-top">
                          <div className="text-slate-800 leading-snug line-clamp-2 break-words" title={fromWhLabel}>
                            {r.from_wh_code ? (
                              <span className="font-semibold">{r.from_wh_code}</span>
                            ) : null}
                            {r.from_wh_name ? (
                              <span className="text-slate-700">
                                {r.from_wh_code ? ' ' : ''}
                                {r.from_wh_name}
                              </span>
                            ) : !r.from_wh_code ? (
                              '—'
                            ) : null}
                          </div>
                        </td>
                        <td className="min-w-0 px-1.5 py-1.5 border border-slate-200 align-top">
                          <select
                            value={itToByRecId[recKey(r.rec_id)] || ''}
                            onChange={(e) =>
                              setItToByRecId((m) => ({ ...m, [recKey(r.rec_id)]: e.target.value }))
                            }
                            className="w-full max-w-full min-h-8 min-w-0 box-border pl-1.5 pr-6 py-1 text-[11px] border border-slate-300 rounded-md bg-white text-slate-800
                                     truncate focus:outline-none focus:ring-1 focus:ring-sap-blue/30 focus:border-sap-blue appearance-none cursor-pointer"
                            title={(() => {
                              const id = itToByRecId[recKey(r.rec_id)];
                              if (!id) return '';
                              const w = itWarehouses.find((x) => String(x.warehouse_id) === String(id));
                              return w ? `${w.wh_code}${w.wh_name ? ` · ${w.wh_name}` : ''}` : '';
                            })()}
                            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.25rem center', backgroundSize: '0.875rem' }}
                          >
                            <option value="">— Destination —</option>
                            {itWarehouses
                              .filter((w) => String(w.warehouse_id) !== String(r.from_warehouse_id ?? ''))
                              .map((w) => (
                                <option key={w.warehouse_id} value={String(w.warehouse_id)}>
                                  {w.wh_code}{w.wh_name ? ` · ${w.wh_name}` : ''}
                                </option>
                              ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-white flex items-center justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={() => {
                  setShowItModal(false);
                  setItToByRecId({});
                  setItModalRowChecked({});
                  setItBulkFromWarehouseId('');
                  setItBulkToValue('');
                }}
                disabled={itSaving}
                className="px-5 py-2 text-xs font-semibold rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={executeInventoryTransfers}
                disabled={itSaving}
                className="px-5 py-2 text-xs font-bold rounded-lg text-white disabled:opacity-40 inline-flex items-center gap-2 transition-colors hover:opacity-95 shadow-sm"
                style={{ backgroundColor: '#4A9AF5' }}
              >
                {itSaving ? (
                  <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : null}
                Save &amp; transfer
              </button>
            </div>
          </div>
        </div>
      )}

      {itSummary && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 p-3">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[min(90vh,720px)] flex flex-col overflow-hidden border border-slate-200/90">
            <div className="flex-1 min-h-0 overflow-y-auto">
              {itSummary.errors.length > 0 && (
                <div className="px-6 py-4 text-xs space-y-2 bg-amber-50 border-b border-amber-200">
                  <div className="font-bold text-amber-900">
                    {itSummary.results.filter((r) => r.transfer_number).length > 0
                      ? `Partial success — ${itSummary.errors.length} line(s) did not transfer`
                      : `Transfer failed — ${itSummary.errors.length} issue(s)`}
                  </div>
                  <ul className="list-disc list-inside text-amber-900/95 space-y-1">
                    {itSummary.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                  <p className="text-2xs text-amber-900/80 leading-relaxed">
                    Transfers use <strong>Available</strong> at the source warehouse only (in_stock − committed in{' '}
                    <span className="font-mono">item_warehouses</span>). Item Master totals can include other warehouses.
                    Re-run MRP after posting if quantities were from an older run.
                  </p>
                </div>
              )}
              {itSummary.results
                .filter((r) => r.transfer_number)
                .map((row, i) => {
                  const postingDate = new Date().toISOString().slice(0, 10);
                  const qty = row.quantity != null ? Number(row.quantity) : null;
                  const qtyStr =
                    qty != null && Number.isFinite(qty)
                      ? qty.toLocaleString('en-IN', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
                      : '—';
                  const uom = row.uom != null && String(row.uom).trim() ? String(row.uom).trim() : '—';
                  const closeIt = () => setItSummary(null);
                  return (
                    <div
                      key={`${row.rec_id}-${row.transfer_number}-${i}`}
                      className={i > 0 ? 'border-t border-slate-200' : ''}
                    >
                      <div
                        className="px-6 py-4 flex items-start gap-3 text-white shrink-0"
                        style={{ backgroundColor: '#4A9AF5' }}
                      >
                        <div
                          className="w-11 h-11 rounded-full border-2 border-white flex items-center justify-center shrink-0 bg-transparent"
                          aria-hidden
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-white">
                            <path
                              d="M20 6L9 17l-5-5"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div className="min-w-0 pt-0.5">
                          <h2 className="text-base font-bold leading-tight">Inventory Transfer Created Successfully!</h2>
                          <p className="text-sm text-white/95 mt-1">
                            {row.transfer_id ? (
                              <button
                                type="button"
                                onClick={() => {
                                  navigate(`/inventory/transfer/${row.transfer_id}`);
                                  setItSummary(null);
                                }}
                                className="text-left font-medium underline-offset-2 hover:underline"
                              >
                                Transfer #{row.transfer_number}
                              </button>
                            ) : (
                              <>Transfer #{row.transfer_number}</>
                            )}
                            <span className="text-sap-textLight font-normal"> — {postingDate}</span>
                          </p>
                        </div>
                      </div>
                      <div className="px-6 py-5 text-xs">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-4 mb-4">
                          <div>
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                              Transfer no.
                            </div>
                            <div className="text-sm font-bold text-slate-900 mt-1.5">
                              {row.transfer_id ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigate(`/inventory/transfer/${row.transfer_id}`);
                                    setItSummary(null);
                                  }}
                                  className="text-[#059669] hover:text-[#047857] hover:underline"
                                >
                                  {row.transfer_number}
                                </button>
                              ) : (
                                row.transfer_number
                              )}
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                              Posting date
                            </div>
                            <div className="text-sm font-bold text-slate-900 mt-1.5">{postingDate}</div>
                          </div>
                          <div>
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                              Status
                            </div>
                            <div className="mt-1.5">
                              <span
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border"
                                style={{ backgroundColor: '#ECFDF5', color: '#047857', borderColor: '#A7F3D0' }}
                              >
                                Open
                              </span>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500 mb-5">
                          Header:{' '}
                          <span className="font-bold text-sap-blue" title={itWarehouses.find(w => w.wh_code === row.from_wh_code)?.wh_name || ''}>
                            {row.from_wh_code}
                            {(() => {
                              const w = itWarehouses.find(x => x.wh_code === row.from_wh_code);
                              return w?.wh_name ? ` · ${w.wh_name}` : '';
                            })()}
                          </span>
                          <span className="text-slate-400 mx-1.5">→</span>
                          <span className="font-bold text-sap-blue" title={itWarehouses.find(w => w.wh_code === row.to_wh_code)?.wh_name || ''}>
                            {row.to_wh_code}
                            {(() => {
                              const w = itWarehouses.find(x => x.wh_code === row.to_wh_code);
                              return w?.wh_name ? ` · ${w.wh_name}` : '';
                            })()}
                          </span>
                        </p>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                          Transfer lines
                        </p>
                        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                          <table className="w-full text-xs">
                            <thead className="bg-slate-50 border-b border-slate-200">
                              <tr className="text-slate-500 text-[10px] uppercase tracking-wide font-semibold">
                                <th className="text-left py-2.5 px-3 w-8">#</th>
                                <th className="text-left py-2.5 px-3">Item no.</th>
                                <th className="text-left py-2.5 px-3">Description</th>
                                <th className="text-center py-2.5 px-2">From</th>
                                <th className="text-center py-2.5 px-1 w-8"> </th>
                                <th className="text-center py-2.5 px-2">To</th>
                                <th className="text-right py-2.5 px-3">Qty</th>
                                <th className="text-right py-2.5 px-3">UOM</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-t border-slate-100">
                                <td className="py-3 px-3 text-slate-500 tabular-nums">1</td>
                                <td className="py-3 px-3">
                                  {row.item_no ? (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        navigate(`/item-master/${encodeURIComponent(row.item_no)}`);
                                        closeIt();
                                      }}
                                      className="font-semibold text-[#059669] hover:text-[#047857] hover:underline text-left"
                                    >
                                      {row.item_no}
                                    </button>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td className="py-3 px-3 text-slate-800 max-w-[220px]">
                                  <span className="line-clamp-2" title={row.item_desc || ''}>
                                    {row.item_desc || '—'}
                                  </span>
                                </td>
                                <td className="py-3 px-2 text-center font-bold whitespace-nowrap" style={{ color: '#4A9AF5' }} title={itWarehouses.find(w => w.wh_code === row.from_wh_code)?.wh_name || ''}>
                                  {row.from_wh_code || '—'}
                                </td>
                                <td className="py-3 px-1 text-center text-slate-400">→</td>
                                <td className="py-3 px-2 text-center font-bold whitespace-nowrap" style={{ color: '#4A9AF5' }} title={itWarehouses.find(w => w.wh_code === row.to_wh_code)?.wh_name || ''}>
                                  {row.to_wh_code || '—'}
                                </td>
                                <td className="py-3 px-3 text-right tabular-nums text-slate-900 font-medium">{qtyStr}</td>
                                <td className="py-3 px-3 text-right text-slate-600">{uom}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-3">1 item transferred</p>
                      </div>
                    </div>
                  );
                })}
              {itSummary.errors.length > 0 && itSummary.results.filter((r) => r.transfer_number).length > 0 ? (
                <div className="px-6 py-3 text-2xs text-slate-600 bg-slate-50 border-t border-slate-200">
                  Lines that succeeded are Released; failures stay Planned — fix stock or warehouse rows, then retry or re-run MRP.
                </div>
              ) : null}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-white flex justify-end shrink-0">
              <button
                type="button"
                onClick={() => setItSummary(null)}
                className="btn-primary"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Accept success summary ── */}
      {acceptSummary && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 p-3">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[min(90vh,720px)] flex flex-col overflow-hidden border border-slate-200/90">
            <div className="px-6 py-5 shrink-0 text-white bg-sap-blue border-b border-sap-border">
              <h2 className="text-base font-bold tracking-tight">Recommendations accepted</h2>
              <p className="text-xs text-white/90 mt-2">
                Purchase lines with the same vendor were combined into one purchase order per vendor.
              </p>
            </div>
            <div className="px-4 py-4 overflow-y-auto flex-1 min-h-0 space-y-4">
              {acceptSummary.purchase_orders.map((po) => (
                <div key={po.po_number} className="rounded-xl border border-slate-200 overflow-hidden">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                    <p className="text-sm font-bold text-slate-900">
                      Purchase Order <span className="text-sap-blue">{po.po_number}</span>
                    </p>
                    <p className="text-xs text-slate-600 mt-1">
                      Vendor: <strong>{po.vendor_code}</strong>
                      {po.vendor_name ? ` — ${po.vendor_name}` : ''}
                      {po.delivery_date ? ` · Delivery: ${new Date(po.delivery_date).toLocaleDateString('en-GB')}` : ''}
                      {po.line_count ? ` · ${po.line_count} line${po.line_count !== 1 ? 's' : ''}` : ''}
                    </p>
                  </div>
                  <ul className="divide-y divide-slate-100">
                    {(po.lines || []).map((ln) => (
                      <li key={ln.rec_id} className="px-4 py-2 text-xs flex justify-between gap-3">
                        <span>
                          <strong className="text-slate-800">{ln.item_no}</strong>
                          {ln.item_desc ? <span className="text-slate-500"> — {ln.item_desc}</span> : null}
                        </span>
                        <span className="text-slate-700 tabular-nums shrink-0">Qty {ln.quantity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {acceptSummary.production_orders.map((po) => (
                <div key={po.order_no} className="rounded-xl border border-purple-200 overflow-hidden">
                  <div className="px-4 py-3 bg-purple-50 border-b border-purple-100">
                    <p className="text-sm font-bold text-slate-900">
                      Production Order <span className="text-purple-700">{po.order_no}</span>
                    </p>
                    <p className="text-xs text-slate-600 mt-1">
                      {po.item_no}{po.item_desc ? ` — ${po.item_desc}` : ''}
                      {po.quantity != null ? ` · Qty ${po.quantity}` : ''}
                    </p>
                    {(po.linked_so_number || po.project_no || po.distr_rule) && (
                      <p className="text-xs text-slate-500 mt-1">
                        {po.linked_so_number ? `Linked SO: ${po.linked_so_number}` : ''}
                        {po.project_no ? `${po.linked_so_number ? ' · ' : ''}Project: ${po.project_no}` : ''}
                        {po.distr_rule ? ` · Distr: ${po.distr_rule}` : ''}
                      </p>
                    )}
                  </div>
                </div>
              ))}
              {acceptSummary.skippedIt > 0 && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  {acceptSummary.skippedIt} inventory transfer line(s) were not processed — use Inventory transfer.
                </p>
              )}
              {acceptSummary.errors.length > 0 && (
                <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {acceptSummary.errors.map((e) => `${e.item_no || ''}: ${e.message}`).join('; ')}
                </p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-white flex justify-end shrink-0">
              <button
                type="button"
                onClick={() => setAcceptSummary(null)}
                className="btn-primary"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Vendor Assignment Modal ── */}
      {showVendorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 text-sap-text border-b border-sap-border">
              <h2 className="text-sm font-semibold">Assign Vendors for Purchase Orders</h2>
              <p className="text-2xs opacity-75 mt-0.5">
                Confirm vendor per line. Lines with the same vendor will be combined into one purchase order.
              </p>
            </div>
            <div className="p-4 max-h-96 overflow-y-auto space-y-2">
              {[...selected]
                .map(id => recs.find(r => r.rec_id === id))
                .filter(r => r && r.status === 'Planned' && PURCHASE_DOC_TYPES.includes(r.doc_type))
                .map(rec => {
                  const selectedVendorId = modalVendorMap[rec.rec_id] || (rec.vendor_id ? String(rec.vendor_id) : '');
                  const selectedVendor = vendors.find(v => String(v.vendor_id) === selectedVendorId);
                  const vendorName = selectedVendor ? selectedVendor.vendor_name : '';
                  return (
                    <div key={rec.rec_id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-slate-800">{rec.item_no}</div>
                        <div className="text-2xs text-slate-500 truncate">{rec.item_desc}</div>
                        {vendorName && (
                          <div className="text-2xs text-emerald-600 font-medium mt-0.5 truncate" title={vendorName}>
                            Vendor: {vendorName}
                          </div>
                        )}
                      </div>
                      <SearchableSelect
                        className="w-64 shrink-0"
                        value={selectedVendorId}
                        onChange={(vid) => setModalVendorMap((m) => ({ ...m, [rec.rec_id]: vid }))}
                        options={modalVendorOptions}
                        emptyLabel="— Vendor —"
                        searchPlaceholder="Search vendor…"
                        usePortal
                        menuMinWidth={320}
                        buttonClassName="min-h-8 text-xs border-slate-300 rounded-lg"
                      />
                    </div>
                  );
                })}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center gap-2 justify-end">
              <button onClick={() => setShowVendorModal(false)}
                className="px-5 py-1.5 text-xs border border-slate-300 rounded-lg hover:bg-white transition-colors text-slate-600">
                Cancel
              </button>
              <button
                onClick={() => { setShowVendorModal(false); executeAccept(modalVendorMap); }}
                className="btn-primary">
                Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Row component ── */
function RecRow({
  rec, idx, checked, onCheck, onNavigateItem, onNavigateWhse,
  vendorOptions, onUpdateRec, saving, isSuperAdmin, onDelete,
}) {
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '—';
  const price = parseFloat(rec.unit_price) || 0;
  const editable = rec.status === 'Planned';
  const showVendorPicker = editable && rec.doc_type !== 'Inventory Transfer';

  const vendorSelectValue = (() => {
    if (rec.vendor_id) return String(rec.vendor_id);
    const code = String(rec.vendor_code || rec.preferred_vendor || '').trim();
    if (!code) return '';
    const match = vendorOptions.find(
      (o) => o.buttonLabel === code
        || String(o.label || '').startsWith(`${code} `)
        || String(o.label || '').startsWith(`${code}—`)
        || String(o.label || '').startsWith(`${code} —`)
    );
    return match ? match.value : '';
  })();

  const DOC_STYLES = {
    'Purchase Order': 'bg-blue-50 text-blue-700',
    'Production Order': 'bg-purple-50 text-purple-700',
    'Inventory Transfer': 'bg-amber-50 text-amber-700',
    'Purchase Quotation': 'bg-teal-50 text-teal-700',
    'Purchase Request': 'bg-green-50 text-green-700',
  };
  const PROC_STYLES = {
    Buy: 'bg-sap-blueMuted text-sap-blueDeeper',
    Make: 'bg-[#F1EEF6] text-[#7B6FA0]',
  };
  const STATUS_STYLES = {
    Planned: 'bg-[#FEF5E7] text-[#B8860B]',
    Released: 'bg-[#EDF2F7] text-[#4A6FA5]',
    Closed: 'bg-[#F0F4F3] text-[#6B7F7F]',
  };

  const procMismatch = rec.item_procurement && rec.procurement_method &&
    rec.item_procurement !== rec.procurement_method;

  return (
    <tr className={`border-t border-sap-border/40 transition-colors ${checked ? 'bg-sap-blue/5' : 'hover:bg-sap-blue/5'}`}>
      {/* Create */}
      <td className="px-1 py-0.5 text-center">
        <input type="checkbox" checked={checked} onChange={onCheck} className="accent-blue-500 w-3.5 h-3.5" />
      </td>
      {/* Order Type */}
      <td className="px-1 py-0.5 whitespace-nowrap min-w-[155px] w-[155px]">
        {editable ? (
          <select
            value={rec.doc_type || DOC_TYPE_OPTIONS[0]}
            disabled={saving || !!rec.freezed}
            onChange={(e) => onUpdateRec(rec.rec_id, { doc_type: e.target.value })}
            className={`w-full max-w-[150px] h-7 px-1.5 text-2xs font-semibold border border-slate-300 rounded-md bg-white
                        focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 ${DOC_STYLES[rec.doc_type] || ''}`}
            title="Change order type for this recommendation only"
          >
            {DOC_TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        ) : (
          <span className={`inline-block px-2 py-0.5 rounded text-2xs font-semibold ${DOC_STYLES[rec.doc_type] || 'bg-slate-50 text-slate-600'}`}>
            {rec.doc_type}
          </span>
        )}
        {procMismatch && (
          <span className="inline-block ml-1 px-1 py-0.5 rounded text-2xs font-semibold bg-amber-100 text-amber-700"
            title={`Item is now "${rec.item_procurement}" but recommendation was generated as "${rec.procurement_method}". Re-run MRP.`}>⚠</span>
        )}
      </td>
      {/* Item Number */}
      <td className="px-1 py-0.5 whitespace-nowrap">
        <span className="inline-flex items-center gap-1">
          <button
            onClick={() => onNavigateItem(rec.item_no)}
            className="flex-shrink-0 text-amber-500 hover:text-amber-600 transition-colors"
            title={`Open Item Master: ${rec.item_no}`}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor">
              <path d="M3 8a.5.5 0 01.5-.5h7.793L9.146 5.354a.5.5 0 11.708-.708l3 3a.5.5 0 010 .708l-3 3a.5.5 0 01-.708-.708L11.293 8.5H3.5A.5.5 0 013 8z" />
            </svg>
          </button>
          <span className="font-semibold text-blue-600 cursor-pointer hover:underline"
            onClick={() => onNavigateItem(rec.item_no)}>{rec.item_no}</span>
        </span>
      </td>
      {/* Item Description */}
      <td className="px-1 py-0.5 text-slate-700 max-w-[160px] truncate" title={rec.item_desc}>{rec.item_desc}</td>
      {/* Quantity */}
      <td
        className="px-1 py-0.5 text-right font-semibold whitespace-nowrap tabular-nums"
        title={inventoryTransferQtyHint(rec)}
      >
        <QtyWithShortfall rec={rec} />
      </td>
      {/* UoM Code */}
      <td className="px-1 py-0.5 text-center text-slate-600 whitespace-nowrap">{rec.uom || '—'}</td>
      {/* UoM Name */}
      <td className="px-1 py-0.5 text-center text-slate-600 whitespace-nowrap">{rec.uom_name || rec.uom || '—'}</td>
      {/* Price */}
      <td className="px-1 py-0.5 text-right text-slate-600 whitespace-nowrap">{price.toFixed(4)}</td>
      {/* Vendor Code / selection */}
      <td className="px-1 py-0.5 text-slate-600 whitespace-nowrap min-w-[110px] w-[110px]">
        {showVendorPicker ? (
          <SearchableSelect
            className="w-full"
            value={vendorSelectValue}
            onChange={(vid) => onUpdateRec(rec.rec_id, { vendor_id: vid ? parseInt(vid, 10) : null })}
            options={vendorOptions}
            emptyLabel="— Select vendor —"
            disabled={saving || !!rec.freezed}
            buttonClassName="min-h-7 text-2xs border-slate-300"
            listMaxHeight="min(240px, 40vh)"
            usePortal
            menuMinWidth={300}
            searchPlaceholder="Search vendor…"
          />
        ) : (
          rec.vendor_code || rec.preferred_vendor || '—'
        )}
      </td>
      {/* Vendor Name */}
      <td className="px-1 py-0.5 text-slate-600 min-w-[180px] max-w-[280px] truncate" title={rec.vendor_name || ''}>
        {rec.vendor_name || (rec.preferred_vendor && !rec.vendor_code ? rec.preferred_vendor : '—')}
      </td>
      {/* MRP Procurement Method */}
      <td className="px-1 py-0.5 text-center whitespace-nowrap">
        <span className={`inline-block px-2 py-0.5 rounded text-2xs font-semibold ${PROC_STYLES[rec.item_procurement] || 'bg-[#F0F4F3] text-[#6B7F7F]'}`}>
          {rec.item_procurement || '—'}
        </span>
      </td>
      {/* MRP Order Interval */}
      <td className="px-1 py-0.5 text-right text-slate-600 whitespace-nowrap">{rec.order_interval ?? 0}</td>
      {/* MRP Order Multiple */}
      <td className="px-1 py-0.5 text-right text-slate-600 whitespace-nowrap">{Number(rec.order_multiple ?? 0).toFixed(3)}</td>
      {/* MRP Minimum Order Qty */}
      <td className="px-1 py-0.5 text-right text-slate-600 whitespace-nowrap">{Number(rec.min_order_qty ?? 0).toFixed(3)}</td>
      {/* MRP Lead Time */}
      <td className="px-1 py-0.5 text-right text-slate-600 whitespace-nowrap">{rec.lead_time_days ?? 0}</td>
      {/* MRP Tolerance Days */}
      <td className="px-1 py-0.5 text-right text-slate-600 whitespace-nowrap">{rec.tolerance_days ?? 0}</td>
      {/* MRP Component Warehouse */}
      <td className="px-1 py-0.5 text-center text-slate-600 whitespace-nowrap">{rec.to_wh_code || '—'}</td>
      {/* Release Date */}
      <td className="px-1 py-0.5 text-center text-slate-600 whitespace-nowrap">{fmtDate(rec.release_date)}</td>
      {/* Due Date */}
      <td className="px-1 py-0.5 text-center text-slate-600 whitespace-nowrap">{fmtDate(rec.required_date)}</td>
      {/* Linked SO / Project / Distr — inherited from sales order demand */}
      <td className="px-1 py-0.5 text-slate-700 whitespace-nowrap font-mono text-2xs">
        {rec.linked_so_number || (rec.linked_so_id ? `#${rec.linked_so_id}` : '—')}
      </td>
      <td className="px-1 py-0.5 text-slate-700 whitespace-nowrap font-mono text-2xs">
        {rec.inherited_project_no || '—'}
      </td>
      <td className="px-1 py-0.5 text-slate-700 whitespace-nowrap font-mono text-2xs">
        {rec.inherited_distr_rule || '—'}
      </td>
      {/* Discount % */}
      <td className="px-1 py-0.5 text-right text-slate-600 whitespace-nowrap">0.0000</td>
      {/* Price after Discount */}
      <td className="px-1 py-0.5 text-right text-slate-600 whitespace-nowrap">{price.toFixed(4)}</td>
      {/* From Whse */}
      <td className="px-1 py-0.5 text-center text-slate-600 whitespace-nowrap">{rec.from_wh_code || '—'}</td>
      {/* To Whse */}
      <td className="px-1 py-0.5 whitespace-nowrap">
        <span className="inline-flex items-center gap-1">
          <button
            onClick={() => onNavigateWhse(rec.item_no)}
            className="flex-shrink-0 text-amber-500 hover:text-amber-600 transition-colors"
            title={`View inventory for ${rec.to_wh_code || 'item'}`}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor">
              <path d="M3 8a.5.5 0 01.5-.5h7.793L9.146 5.354a.5.5 0 11.708-.708l3 3a.5.5 0 010 .708l-3 3a.5.5 0 01-.708-.708L11.293 8.5H3.5A.5.5 0 013 8z" />
            </svg>
          </button>
          <span className="text-slate-600 cursor-pointer hover:underline"
            onClick={() => onNavigateWhse(rec.item_no)}>{rec.to_wh_code || '\u2014'}</span>
        </span>
      </td>
      {/* Qty (Inventory UoM) */}
      <td
        className="px-1 py-0.5 text-right font-semibold whitespace-nowrap tabular-nums"
        title={inventoryTransferQtyHint(rec)}
      >
        <QtyWithShortfall rec={rec} />
      </td>
      {/* Items per Unit */}
      <td className="px-1 py-0.5 text-right text-slate-600 whitespace-nowrap">1</td>
      {/* Exception */}
      <td className="px-1 py-0.5 text-center text-slate-400 whitespace-nowrap">—</td>
      {/* Blanket Agreement No. */}
      <td className="px-1 py-0.5 text-center text-slate-400 whitespace-nowrap">—</td>
      {/* Critical Item Delivery Date — the planner's own date, saved as it is picked */}
      <td className="px-1 py-0.5 text-center whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        <input
          type="date"
          value={toDateInputValue(rec.critical_item_delivery_date)}
          disabled={saving || rec.status !== 'Planned'}
          onChange={(e) => onUpdateRec(rec.rec_id, { critical_item_delivery_date: e.target.value || null })}
          className="w-[120px] px-1 py-0.5 text-[10px] border border-slate-300 rounded
            focus:outline-none focus:ring-1 focus:ring-sap-blue disabled:bg-slate-50 disabled:text-slate-400"
          title="Date this item is needed. Leave empty if the run's due date is enough."
        />
      </td>
    </tr>
  );
}